import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { backendServices, useGetSupplierSIMRequest } from 'common';
import { derivePostSignupFlags } from './derivePostSignupFlags';
import { PendingSimRequest } from './steps/ReviewAndSubmit';

export interface SupplierPendingSimRequestFlags {
    /** Buyer has at least one pending W-9 / Tax Details request for this supplier */
    taxDetails: boolean;
    /** Buyer has at least one pending Certificate of Insurance request for this supplier */
    certificateOfInsurance: boolean;
    /** Buyer has at least one pending Remittance Information request for this supplier */
    remittance: boolean;
}

export const openSimRequestParams = (supplierCompanyId: number) => ({
    companyId: supplierCompanyId,
    isBuyer: false,
    $filter: 'DateUploaded eq null',
});

/**
 * Loads the SIM schedules that apply to a supplier and the supplier's open
 * reminders, and derives which post-signup steps to render. Only schedules
 * with an open reminder from this buyer become steps.
 */
export function useSupplierPendingSimRequests({
    supplierCompanyId,
    buyerCompanyId,
    enabled = true,
}: {
    supplierCompanyId: number | null;
    buyerCompanyId: number | null;
    enabled?: boolean;
}) {
    const shouldFetch = enabled && supplierCompanyId != null;

    const schedulesQuery = useQuery(
        ['SimSchedules', supplierCompanyId],
        async () => {
            const api = new backendServices.Apis.DocumentTypeApi();
            const res = await api.searchDocumentTypes({ companyId: supplierCompanyId!, isBuyer: false });
            return res.data;
        },
        { enabled: shouldFetch, staleTime: Infinity, cacheTime: 0 }
    );

    // Fresh on every visit, never refetched during one, so the steps can't change mid-flow.
    const requestsQuery = useGetSupplierSIMRequest(openSimRequestParams(supplierCompanyId!), {
        enabled: shouldFetch,
        staleTime: Infinity,
        cacheTime: 0,
    });

    // Latest open reminder per schedule, so a re-request is the one the submitted document completes.
    const simRequests = useMemo<PendingSimRequest[]>(() => {
        const latestRequestIdBySchedule = new Map<number, number>();
        (requestsQuery.data ?? [])
            .filter((request) => request.BuyerCompanyId === buyerCompanyId)
            .forEach((request) => {
                const latestRequestId = latestRequestIdBySchedule.get(request.SIMDocumentTypeId);
                if (latestRequestId == null || request.Id > latestRequestId) {
                    latestRequestIdBySchedule.set(request.SIMDocumentTypeId, request.Id);
                }
            });
        return Array.from(latestRequestIdBySchedule, ([SIMDocumentTypeId, SupplierSIMRequestId]) => ({
            SIMDocumentTypeId,
            SupplierSIMRequestId,
        }));
    }, [requestsQuery.data, buyerCompanyId]);

    const schedules = useMemo(() => {
        const openSimDocumentTypeIds = new Set(simRequests.map((request) => request.SIMDocumentTypeId));
        return (schedulesQuery.data ?? []).filter((schedule) => openSimDocumentTypeIds.has(schedule.Id));
    }, [schedulesQuery.data, simRequests]);

    const { flags, activeDocumentClasses } = useMemo(() => derivePostSignupFlags(schedules), [schedules]);

    return {
        flags,
        activeDocumentClasses,
        // Schedules with an open reminder, for per-class config (attachment options; also the TEMPORARY customer workaround).
        schedules,
        simRequests,
        hasAnySupplementalRequest: flags.taxDetails || flags.certificateOfInsurance || flags.remittance,
        isLoading: shouldFetch && (schedulesQuery.isLoading || requestsQuery.isLoading),
        isError: schedulesQuery.isError || requestsQuery.isError,
    };
}
